import { decodeEntities } from '../utils/decodeEntities';
import type { VideoPlatform } from '../types';

// CRA build-time env shim for TypeScript (no eslint rule refs)
declare const process: { env?: Record<string, string | undefined> };

interface BasicMeta {
  platform: VideoPlatform | 'Other';
  video_url: string;
  thumbnail_url: string; // may be 'N/A'
  title: string;
  creator_name: string;
  description?: string; // optional text/caption when available
  views: number;
  likes: number;
  embed_html?: string; // provider HTML for modal playback (optional)
  provider_thumbnail_url?: string; // raw provider thumbnail when available
}

/* ---------------- platform detection & normalization ---------------- */

function detectPlatform(url: string): VideoPlatform | 'Other' {
  try {
    const h = new URL(url).hostname.toLowerCase();
    if (h.includes('youtube') || h === 'youtu.be') return 'YouTube';
    if (h.includes('tiktok')) return 'TikTok';
    if (h.includes('instagram') || h === 'instagr.am') return 'Instagram';
    if (h.includes('facebook') || h.includes('fb.watch') || h === 'm.facebook.com') return 'Facebook';
  } catch {}
  return 'Other';
}

function normalizeYouTube(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace('/', '');
      return id ? `https://www.youtube.com/watch?v=${id}` : url;
    }
    if (u.pathname.startsWith('/shorts/')) {
      const id = u.pathname.split('/')[2];
      return id ? `https://www.youtube.com/watch?v=${id}` : url;
    }
    // Strip time params etc. (keep v)
    if (u.searchParams.get('v')) {
      return `https://www.youtube.com/watch?v=${u.searchParams.get('v')}`;
    }
  } catch {}
  return url;
}

function normalizeInstagram(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    if ((parts[0] === 'reel' || parts[0] === 'reels' || parts[0] === 'p') && parts[1]) {
      return `https://www.instagram.com/${parts[0] === 'reels' ? 'reel' : parts[0]}/${parts[1]}/`;
    }
  } catch {}
  return url;
}

function normalizeFacebook(url: string): string {
  try {
    const u = new URL(url);
    const host = 'https://www.facebook.com';
    if (u.pathname.startsWith('/reel/')) {
      const id = u.pathname.split('/')[2];
      return id ? `${host}/reel/${id}/` : url;
    }
    if (u.pathname.startsWith('/watch/') && u.searchParams.get('v')) {
      const v = u.searchParams.get('v')!;
      return `${host}/watch/?v=${v}`;
    }
    // fb.watch short-links: leave as-is (Graph resolves)
  } catch {}
  return url;
}

function normalizeTikTok(url: string): string {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/@[^/]+\/video\/(\d+)/);
    if (m?.[1]) return `https://www.tiktok.com${m[0]}`;
  } catch {}
  return url;
}

/* ---------------- fetch helpers ---------------- */

function sleep(ms: number) { return new Promise(r => setTimeout(r, ms)); }

async function fetchJSON(endpoint: string, opts?: { timeoutMs?: number; signal?: AbortSignal }): Promise<any | null> {
  const timeoutMs = opts?.timeoutMs ?? 8000;
  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort();
  if (opts?.signal) opts.signal.addEventListener('abort', onAbort, { once: true });
  const t = setTimeout(() => ctrl.abort(), timeoutMs);

  try {
    const res = await fetch(endpoint, { credentials: 'omit', signal: ctrl.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(t);
    if (opts?.signal) opts.signal.removeEventListener('abort', onAbort);
  }
}

async function fetchWithRetry(endpoints: string[], signal?: AbortSignal): Promise<any | null> {
  // Try each endpoint up to 2 attempts (0, 1) with small backoff
  for (const ep of endpoints) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const j = await fetchJSON(ep, { timeoutMs: 8000 + attempt * 1500, signal });
      if (j) return j;
      await sleep(120 * (1 + Math.random()) * (attempt + 1)); // jitter
      if (signal?.aborted) return null;
    }
  }
  return null;
}

/* ---------------- main ---------------- */

export async function fetchBasicMeta(inputUrl: string, options?: { signal?: AbortSignal }): Promise<BasicMeta> {
  let platform = detectPlatform(inputUrl);
  let videoUrl = inputUrl;

  if (platform === 'YouTube') videoUrl = normalizeYouTube(inputUrl);
  if (platform === 'Instagram') videoUrl = normalizeInstagram(inputUrl);
  if (platform === 'Facebook') videoUrl = normalizeFacebook(inputUrl);
  if (platform === 'TikTok') videoUrl = normalizeTikTok(inputUrl);

  // Same-origin or configured proxy base. If OEMBED base not set, fall back to Functions base to support prefixes like /api
  const env = (typeof process !== 'undefined' && process && process.env) ? (process.env as Record<string, string | undefined>) : {};
  const FN_BASE = (env.REACT_APP_FUNCTIONS_BASE || '').replace(/\/$/, '');
  const OEMBED_BASE = (env.REACT_APP_OEMBED_BASE || FN_BASE).replace(/\/$/, '');
  const BASE = OEMBED_BASE || '';
  // Optional per-platform overrides (if you prefer explicit proxies)
  const TT_PROXY_ENV = (env.REACT_APP_TIKTOK_OEMBED_PROXY || '').replace(/\s+$/, '');
  const IG_PROXY_ENV = (env.REACT_APP_IG_OEMBED_PROXY || '').replace(/\s+$/, '');
  const FB_PROXY_ENV = (env.REACT_APP_FB_OEMBED_PROXY || '').replace(/\s+$/, '');
  // Build proxies; when BASE is empty, this still yields same-origin "/oembed?..."
  const IG_PROXY = IG_PROXY_ENV || `${BASE}/oembed?platform=instagram&url=`;
  const FB_PROXY = FB_PROXY_ENV || `${BASE}/oembed?platform=facebook&url=`;
  const TT_PROXY = TT_PROXY_ENV || `${BASE}/oembed?platform=tiktok&url=`;
  // YouTube: do NOT fall back to BASE; use direct provider unless explicit YT proxy is set
  const YT_PROXY = (env.REACT_APP_YT_OEMBED_PROXY || '').replace(/\/$/, '');

  // Build provider endpoints (primary) and fetch
  const providerEndpoints: string[] = [];
  if (platform === 'YouTube') {
    // Always try configured proxy first (if set), then provider
    if (YT_PROXY) providerEndpoints.push(`${YT_PROXY}${encodeURIComponent(videoUrl)}`);
    providerEndpoints.push(`https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`);
  } else if (platform === 'TikTok') {
    // Always proxy first (if set); then provider as fallback
    if (TT_PROXY) providerEndpoints.push(`${TT_PROXY}${encodeURIComponent(videoUrl)}`);
    providerEndpoints.push(`https://www.tiktok.com/oembed?url=${encodeURIComponent(videoUrl)}`);
  } else if (platform === 'Instagram') {
    // Always proxy first (Instagram oEmbed requires app/domain)
    let domain = '';
    if (typeof window !== 'undefined') {
      const h = window.location.hostname;
      if (!/^localhost$|^127\.0\.0\.1$/.test(h)) {
        domain = `&domain=${encodeURIComponent(window.location.host)}`;
      }
    }
    if (IG_PROXY) providerEndpoints.push(`${IG_PROXY}${encodeURIComponent(videoUrl)}${domain}`);
    // No direct provider fallback (requires app tokens), rely on noembed below if needed
  } else if (platform === 'Facebook') {
    // Always proxy first (FB oEmbed requires app/domain)
    if (FB_PROXY) providerEndpoints.push(`${FB_PROXY}${encodeURIComponent(videoUrl)}`);
    // No direct provider fallback
  }

  // Log provider endpoints in development (browser only)
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    try {
      console.info('[oEmbed] provider endpoints (proxy-first):', providerEndpoints);
    } catch {}
  }

  const primary = await fetchWithRetry(providerEndpoints, options?.signal);

  // If missing thumbnail (common with Facebook), try noembed to fill gaps
  const missingThumb = !primary || (!primary.thumbnail_url && !primary.thumbnail_url_with_play_button && !primary.thumbnail_url_sd && !primary.thumbnail_url_large);
  const missingTitleOrAuthor = !primary || (!primary.title && !primary.author_name);
  const shouldFetchNoembed = missingThumb || missingTitleOrAuthor;
  const noembedEndpoint = `https://noembed.com/embed?url=${encodeURIComponent(videoUrl)}`;
  const fallback = shouldFetchNoembed ? await fetchWithRetry([noembedEndpoint], options?.signal) : null;

  // Map to stable fields
  let title = (primary?.title ?? fallback?.title ?? '').toString() || 'N/A';
  let creator =
    (primary?.author_name ??
      fallback?.author_name ??
      primary?.provider_name ??
      fallback?.provider_name ??
      primary?.author_url?.replace(/^https?:\/\/(www\.)?/, '') ??
      fallback?.author_url?.replace(/^https?:\/\/(www\.)?/, '') ??
      '') || 'N/A';
  let thumb =
    primary?.thumbnail_url ||
    primary?.thumbnail_url_with_play_button ||
    primary?.thumbnail_url_sd ||
    primary?.thumbnail_url_large ||
    fallback?.thumbnail_url ||
    'N/A';
  const providerThumb =
    primary?.thumbnail_url ||
    primary?.thumbnail_url_with_play_button ||
    primary?.thumbnail_url_sd ||
    primary?.thumbnail_url_large ||
    undefined;

  // Provider correction
  const provider = (primary?.provider_name || fallback?.provider_name || '').toString().toLowerCase();
  if (provider.includes('tiktok')) platform = 'TikTok';
  else if (provider.includes('instagram')) platform = 'Instagram';
  else if (provider.includes('facebook')) platform = 'Facebook';
  else if (provider.includes('youtube')) platform = 'YouTube';

  // Provider-specific fallbacks to avoid blank cards
  if (platform === 'Instagram') {
    // IG often returns author_name and thumbnail but empty title
    if (!title || title === 'N/A') {
      const isReel = /\/reel\//i.test(videoUrl);
      title = isReel ? 'Instagram Reel' : 'Instagram Post';
    }
    if (!creator || creator === 'N/A') {
      // Provide a friendly default to avoid "N/A" in UI
      creator = 'Instagram';
    }
  } else if (platform === 'Facebook') {
    // FB oEmbed often lacks title/author/thumbnail
    if (!title || title === 'N/A') {
      const lower = videoUrl.toLowerCase();
      const kind = lower.includes('/reel/') || lower.includes('/watch/') || lower.includes('/videos/') ? 'Reel' : 'Post';
      title = `Facebook ${kind}`;
    }
    if (!creator || creator === 'N/A') {
      creator = 'Facebook Page';
    }
  }

  return {
    platform,
    video_url: videoUrl,
    thumbnail_url: thumb,
    title: decodeEntities(title || ''),
    creator_name: decodeEntities(creator || ''),
  description: decodeEntities(((primary as any)?.description || (fallback as any)?.description || '') as string),
    views: 0,
    likes: 0,
    embed_html: typeof primary?.html === 'string' ? primary.html : undefined,
    provider_thumbnail_url: providerThumb,
  };
}
